import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Clock, Users, MapPin, Briefcase, AlertCircle, Car, User as UserIcon, Star, Wind, Heart, Shield, Check, Share2, MessageCircle, Phone } from 'lucide-react';
import { AiAssistantBubble } from './AiAssistantBubble';
import BookingCountdown from './BookingCountdown';
import { API as API_URL, BACKEND_URL } from '../utils/apiConfig';
import './RideDetailsModal.css';

// Helper function to safely format date using browser locale
const formatDate = (dateStr, langCode) => {
  if (!dateStr) return '-';
  const localeMap = {
    de: 'de-DE', en: 'en-US', tr: 'tr-TR', ar: 'ar-SA',
    pl: 'pl-PL', pt: 'pt-BR', fr: 'fr-FR', it: 'it-IT',
    uk: 'uk-UA', es: 'es-ES'
  };
  const locale = localeMap[langCode] || 'de-DE';
  try {
    const date = new Date(dateStr + 'T00:00:00');
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString(locale);
  } catch (e) {
    return dateStr;
  }
};

export const RideDetailsModal = ({ ride, onClose, onBook, bookingLoading = false }) => {
  const { t, i18n } = useTranslation();
  
  const [selectedSeats, setSelectedSeats] = useState(1);
  const [luggageOptions, setLuggageOptions] = useState({
    suitcase: false,
    backpack: false,
    ski: false,
    bicycle: false
  });
  const [driverStats, setDriverStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [pendingLock, setPendingLock] = useState(null); // { booking_id, lock_expires_at, price_breakdown }
  const [lockExpired, setLockExpired] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  // Load driver stats
  useEffect(() => {
    const loadDriverStats = async () => {
      if (ride?.driver?.id) {
        setLoadingStats(true);
        try {
          const response = await fetch(`${API_URL}/ratings/user/${ride.driver.id}`);
          if (response.ok) {
            const data = await response.json();
            setDriverStats(data);
          }
        } catch (error) {
          console.error('Error loading driver stats:', error);
        } finally {
          setLoadingStats(false);
        }
      }
    };

    if (ride) loadDriverStats();
  }, [ride?.driver?.id, BACKEND_URL]);

  if (!ride) return null;

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  // Calculate values safely — cost-sharing model
  const pricePerSeat = ride.price_per_seat || 0;
  const totalPrice = (Number(pricePerSeat || 0) * selectedSeats).toFixed(2);
  const costPerKm = ride.cost_per_km || 0.30;
  const routeDistance = ride.route_distance_km || ride.routeDistanceKm || 0;
  const totalOperatingCost = ride.total_operating_cost || (routeDistance * costPerKm);
  const driverRating = driverStats?.average_rating || ride.driver?.average_rating || ride.driver?.rating_avg || ride.driver?.rating || 0;
  const driverTotalRatings = driverStats?.total_ratings || ride.driver?.total_ratings || ride.driver?.rating_count || ride.driver?.reviews || 0;
  const driverTotalRides = driverStats?.user_stats?.total_rides || ride.driver?.total_rides || ride.driver?.total_rides_as_driver || 0;
  const driverName = ride.driver_name || ride.driver?.name || `${ride.driver?.firstName || ''} ${ride.driver?.lastName || ''}`.trim() || t('common.driver');
  const destinationCity = ride.destination_city || ride.end_city || '-';
  const seatsAvailable = ride.seats_available ?? 0;
  const seatsTotal = ride.seats_total ?? 0;

  const handleLuggageToggle = (type) => {
    setLuggageOptions(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleBookClick = () => {
    if (onBook) {
      onBook(ride, selectedSeats);
    }
  };

  // Seat-lock booking flow: lock → countdown → confirm
  const handleSeatLock = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setConfirmLoading(true);
    setLockExpired(false);
    try {
      const resp = await fetch(`${API_URL}/bookings/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ride_id: ride.ride_id, seats_booked: selectedSeats }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Booking failed');
      setPendingLock(data.booking);
    } catch (err) {
      alert(err.message);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!pendingLock) return;
    const token = localStorage.getItem('token');
    setConfirmLoading(true);
    try {
      const resp = await fetch(`${API_URL}/bookings/${pendingLock.booking_id}/confirm-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Payment failed');
      alert(`${t('rideDetails.bookingConfirmed') || 'Buchung bestätigt!'}`);
      setPendingLock(null);
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <X size={24} />
        </button>

        <h2 className="modal-title">{t('rideDetails.title').toUpperCase()}</h2>

        <div className="details-grid">
          {/* DRIVER INFO */}
          <div className="detail-section">
            <div className="section-header">
              <UserIcon size={20} />
              <h3>{t('rideDetails.sections.driver').toUpperCase()}</h3>
            </div>
            <div className="section-content">
              <div className="driver-header">
                <div className="driver-avatar">
                  {getInitials(driverName)}
                </div>
                <div className="driver-details">
                  <h4>{driverName}</h4>
                  <div className="driver-rating">
                    {loadingStats ? (
                      <span className="text-gray-500">{t('rideDetails.loadingStats')}</span>
                    ) : (
                      <>
                        <Star size={16} fill="#fbbf24" stroke="#fbbf24" />
                        <span>
                          {driverRating > 0 ? Number(driverRating).toFixed(1) : 'New'} 
                          ({driverTotalRatings} {t('rideDetails.reviews')})
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="driver-stats">
                {ride.driver?.verified && (
                  <span className="verified flex items-center gap-1">
                    <Shield size={14} />
                    {t('rideDetails.verified')}
                  </span>
                )}
                <span>📅 {driverTotalRides} {t('rideDetails.totalRides')}</span>
              </div>
              
              {ride.driver?.languages && (
                <div className="driver-languages">
                  <strong>{t('rideDetails.languages')}:</strong> {ride.driver.languages.join(', ')}
                </div>
              )}
              
              {ride.driver?.bio && (
                <p className="driver-bio">
                  "{ride.driver.bio}"
                </p>
              )}
            </div>
          </div>

          {/* ROUTE & TIME */}
          <div className="detail-section">
            <div className="section-header">
              <MapPin size={20} />
              <h3>{t('rideDetails.sections.route').toUpperCase()}</h3>
            </div>
            <div className="section-content">
              <div className="schedule-item">
                <Clock size={18} />
                <div>
                  <strong>{t('rideDetails.departure')}</strong>
                  <p>{ride.departure_time || '-'} • {formatDate(ride.departure_date, i18n.language)}</p>
                </div>
              </div>
              <div className="route-display">
                <MapPin size={18} className="text-blue-600" />
                <span className="text-blue-600 font-bold">{ride.start_city || '-'}</span>
                <span className="text-gray-400 mx-2">→</span>
                <MapPin size={18} className="text-green-600" />
                <span className="text-green-600 font-bold">{destinationCity}</span>
              </div>
              {ride.intermediate_stops?.length > 0 && (
                <div>
                  <strong>{t('rideDetails.intermediateStops')}:</strong>
                  <p>{ride.intermediate_stops.join(', ')}</p>
                </div>
              )}
            </div>
          </div>

          {/* PRICE & SEATS */}
          <div className="detail-section">
            <div className="section-header">
              <Users size={20} />
              <h3>{t('rideDetails.sections.price').toUpperCase()}</h3>
            </div>
            <div className="section-content">
              <div className="seats-display">
                <Users size={20} />
                <span>{seatsAvailable} {t('rideDetails.seatsAvailable', { total: seatsTotal })}</span>
              </div>
              
              <div className="seat-selector">
                <label className="block text-gray-700 mb-2">{t('rideDetails.numberOfSeats')}:</label>
                <select
                  value={selectedSeats}
                  onChange={(e) => setSelectedSeats(parseInt(e.target.value))}
                  className="w-full p-2 border border-gray-300 rounded-lg mb-4"
                  disabled={bookingLoading || seatsAvailable === 0}
                >
                  {[...Array(Math.max(Math.min(seatsAvailable, 4), 1)).keys()].map(i => (
                    <option key={i+1} value={i+1}>{i+1} {t('common.seats')}</option>
                  ))}
                </select>
              </div>

              <div className="price-display mt-4">
                <div>
                  <div className="price-label">{t('rideDetails.pricePerPerson')}:</div>
                  <div className="price-value">{Number(pricePerSeat || 0).toFixed(2)}€</div>
                </div>
                <div className="text-2xl">×</div>
                <div>
                  <div className="price-label">{selectedSeats} {t('common.seats')}:</div>
                  <div className="price-value">{totalPrice}€</div>
                </div>
              </div>

              {/* Fair Price Transparency Info */}
              <div data-testid="fair-price-info" style={{
                marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '8px',
                background: '#f0fdf4', border: '1px solid #bbf7d0',
                display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                fontSize: '0.82rem', color: '#166534'
              }}>
                <Shield size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>
                  <strong>{t('rideDetails.fairPrice.label')}</strong>{' '}
                  {t('rideDetails.fairPrice.description', {
                    costPerKm: Number(costPerKm || 0).toFixed(2),
                    occupants: (seatsTotal + 1)
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* LUGGAGE OPTIONS */}
          <div className="detail-section">
            <div className="section-header">
              <Briefcase size={20} />
              <h3>{t('rideDetails.sections.luggage').toUpperCase()}</h3>
            </div>
            <div className="section-content">
              <div className="luggage-list">
                <div className="luggage-item" onClick={() => handleLuggageToggle('backpack')}>
                  <input 
                    type="checkbox" 
                    checked={luggageOptions.backpack}
                    readOnly
                  />
                  <span>🎒 {t('rideDetails.handLuggage')}</span>
                </div>
                
                {ride.luggage?.suitcase_allowed && (
                  <div className="luggage-item" onClick={() => handleLuggageToggle('suitcase')}>
                    <input 
                      type="checkbox" 
                      checked={luggageOptions.suitcase}
                      readOnly
                    />
                    <span>🧳 {t('createRide.luggage.suitcase')} (max. {ride.luggage.suitcase_max || 1})</span>
                  </div>
                )}
                
                {ride.luggage?.bicycle_allowed && (
                  <div className="luggage-item" onClick={() => handleLuggageToggle('bicycle')}>
                    <input 
                      type="checkbox" 
                      checked={luggageOptions.bicycle}
                      readOnly
                    />
                    <span>🚲 {t('createRide.luggage.bicycle')}</span>
                  </div>
                )}
                
                {ride.luggage?.ski_allowed && (
                  <div className="luggage-item" onClick={() => handleLuggageToggle('ski')}>
                    <input 
                      type="checkbox" 
                      checked={luggageOptions.ski}
                      readOnly
                    />
                    <span>🎿 {t('createRide.luggage.ski')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* VEHICLE DETAILS */}
          <div className="detail-section">
            <div className="section-header">
              <Car size={20} />
              <h3>{t('rideDetails.sections.vehicle').toUpperCase()}</h3>
            </div>
            <div className="section-content">
              <p className="vehicle-model">
                {ride.vehicle_info?.make && ride.vehicle_info?.model 
                  ? `${ride.vehicle_info.make} ${ride.vehicle_info.model}`
                  : ride.vehicle_info?.model || ride.vehicle_info?.make || t('rideDetails.vehicleNotSpecified')}
              </p>
              <div className="vehicle-features">
                {ride.vehicle_info?.color && (
                  <div className="feature-item">
                    <span>🎨</span>
                    <span>{ride.vehicle_info.color}</span>
                  </div>
                )}
                {ride.vehicle_info?.ac && (
                  <div className="feature-item">
                    <span>❄️</span>
                    <span>{t('createRide.vehicle.ac')}</span>
                  </div>
                )}
                {ride.vehicle_info?.spacious && (
                  <div className="feature-item">
                    <span>📏</span>
                    <span>{t('createRide.vehicle.spacious')}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* PREFERENCES */}
          <div className="detail-section">
            <div className="section-header">
              <Heart size={20} />
              <h3>{t('rideDetails.sections.preferences').toUpperCase()}</h3>
            </div>
            <div className="section-content">
              <div className="rules-list">
                <div className="rule-item">
                  <span className="rule-icon">
                    {ride.preferences?.smoking === 'not_allowed' ? '🚭' : ride.preferences?.smoking === 'allowed' ? '🚬' : '🚭'}
                  </span>
                  <span>{ride.preferences?.smoking === 'not_allowed' ? t('rideDetails.noSmoking') : ride.preferences?.smoking === 'allowed' ? t('rideDetails.smokingAllowed') : t('rideDetails.noSmoking')}</span>
                </div>
                
                <div className="rule-item">
                  <span className="rule-icon">
                    {ride.preferences?.pets === 'none' || !ride.preferences?.pets ? '🚫' : '🐾'}
                  </span>
                  <span>{ride.preferences?.pets === 'none' || !ride.preferences?.pets ? t('rideDetails.noPets') : t('rideDetails.petsAllowed')}</span>
                </div>
                
                <div className="rule-item">
                  <span className="rule-icon">🎵</span>
                  <span>{ride.preferences?.music === 'quiet' ? t('rideDetails.quietMusic') : t('rideDetails.musicOk')}</span>
                </div>
                
                <div className="rule-item">
                  <span className="rule-icon">🗨️</span>
                  <span>
                    {ride.preferences?.conversation === 'quiet' ? t('rideDetails.quietConversation') : 
                     ride.preferences?.conversation === 'chatty' ? t('rideDetails.chatty') : t('rideDetails.musicOk')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SHARE & AI ASSISTANT */}
        <div data-testid="share-ride-section" style={{
          display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap'
        }}>
          <button
            data-testid="ai-assistant-open-btn"
            onClick={() => {
              const bubble = document.querySelector('[data-testid="ai-assistant-bubble"]');
              if (bubble) bubble.click();
            }}
            style={{
              flex: 1, minWidth: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              padding: '0.65rem 1rem', borderRadius: '10px', border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', color: '#fff', fontWeight: 600, fontSize: '0.85rem'
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 8V4H8"/><rect width="16" height="12" x="4" y="8" rx="2"/><path d="M2 14h2"/><path d="M20 14h2"/><path d="M15 13v2"/><path d="M9 13v2"/>
            </svg>
            AI Concierge
          </button>
          <button
            data-testid="share-copy-btn"
            onClick={() => {
              const text = `${ride.start_city} → ${ride.endCity || ride.end_city} — ${Number(pricePerSeat || 0).toFixed(2)}€ ${t('rideDetails.share.perPerson')} — ${window.location.origin}/search`;
              navigator.clipboard.writeText(text).then(() => alert(t('rideDetails.share.copied')));
            }}
            style={{
              flex: 1, minWidth: '140px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              padding: '0.65rem 1rem', borderRadius: '10px', border: '1px solid #e2e8f0', cursor: 'pointer',
              background: '#f8fafc', color: '#1f2937', fontWeight: 600, fontSize: '0.85rem'
            }}
          >
            <Share2 size={18} />
            {t('rideDetails.share.copyLink')}
          </button>
        </div>

        {/* MODAL ACTIONS */}
        {pendingLock && !lockExpired ? (
          <div className="modal-actions" data-testid="seat-lock-section" style={{ flexDirection: 'column', gap: '1rem' }}>
            <BookingCountdown
              lockExpiresAt={pendingLock.lock_expires_at}
              onExpired={() => setLockExpired(true)}
            />
            <div style={{ fontSize: '0.85rem', color: '#64748b', textAlign: 'center' }}>
              {t('rideDetails.seatLocked') || 'Dein Platz ist reserviert. Bitte jetzt bestätigen.'}
            </div>
            <button
              className="btn-primary"
              onClick={handleConfirmPayment}
              disabled={confirmLoading}
              data-testid="confirm-payment-btn"
              style={{ width: '100%' }}
            >
              {confirmLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t('common.loading')}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Check size={20} />
                  {t('rideDetails.confirmPayment') || 'Zahlung bestätigen'} - {pendingLock.price_breakdown?.total || totalPrice}€
                </span>
              )}
            </button>
            <button className="btn-secondary" onClick={() => { setPendingLock(null); }} style={{ width: '100%' }}>
              <X size={18} /> {t('common.cancel')}
            </button>
          </div>
        ) : lockExpired ? (
          <div className="modal-actions" style={{ flexDirection: 'column', gap: '1rem', textAlign: 'center' }}>
            <div style={{ padding: '1rem', background: '#fef2f2', borderRadius: '10px', border: '1px solid #fecaca', color: '#991b1b', fontWeight: 600 }}>
              {t('rideDetails.lockExpired') || 'Reservierung abgelaufen. Bitte erneut versuchen.'}
            </div>
            <button className="btn-primary" onClick={() => { setPendingLock(null); setLockExpired(false); }} style={{ width: '100%' }}>
              {t('common.retry') || 'Erneut versuchen'}
            </button>
          </div>
        ) : (
          <div className="modal-actions">
            <button 
              className="btn-primary" 
              onClick={handleBookClick}
              disabled={bookingLoading || seatsAvailable === 0}
              data-testid="book-and-pay-btn"
            >
              {bookingLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t('rideDetails.booking')}
                </span>
              ) : seatsAvailable === 0 ? (
                <span>{t('rideDetails.fullyBooked')}</span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <Check size={20} />
                  {t('rideDetails.bookAndPay')} - {totalPrice}€
                </span>
              )}
            </button>
            
            <button
              className="btn-secondary"
              onClick={handleSeatLock}
              disabled={confirmLoading || seatsAvailable === 0}
              data-testid="seat-lock-btn"
              style={{ background: '#f0f9ff', borderColor: '#3b82f6', color: '#1d4ed8' }}
            >
              {confirmLoading ? t('common.loading') : (t('rideDetails.reserveSeat') || 'Platz reservieren (3 Min.)')}
            </button>
            
            <button 
              className="btn-secondary"
              onClick={onClose}
              disabled={bookingLoading}
            >
              <X size={18} />
              {t('common.cancel')}
            </button>
          </div>
        )}

        {/* INFO NOTE */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <div className="flex items-start gap-2">
            <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
            <div>
              <strong>{t('common.required')}:</strong> {t('rideDetails.bookingNote')}
            </div>
          </div>
        </div>

        {/* AI Ride Assistant */}
        {ride.ride_id && (
          <AiAssistantBubble
            rideId={ride.ride_id}
            rideSummary={`${ride.start_city || ride.from_city || '?'} → ${ride.end_city || ride.to_city || '?'}`}
          />
        )}
      </div>
    </div>
  );
};
